export type FruitNinjaRemoteUiSnapshot = {
  hudVisible: boolean;
  pauseVisible: boolean;
  gameOverVisible: boolean;
  score: number;
  pauseScore: number;
  gameOverScore: number;
  lives: number | null;
  timeLeftSec: number | null;
  timerUrgent: boolean;
  gameMode: 'CLASSIC' | 'ARCADE' | 'ZEN' | null;
  streak: number;
  comboCount: number | null;
  /** 0…1 доля viewport (после маппинга из игровой зоны); null = нет комбо. */
  comboNormX: number | null;
  comboNormY: number | null;
  /** Player 2 combo (split mode). */
  combo2Count: number | null;
  combo2NormX: number | null;
  combo2NormY: number | null;
  /** From native `bestScore` / `best_score`; omit row when null. */
  bestScore: number | null;
  /** Two-player split HUD. */
  split: boolean;
  player1Score: number | null;
  player1Lives: number | null;
  player1Streak: number | null;
  player2Score: number | null;
  player2Lives: number | null;
  player2Streak: number | null;
  /** Per-player game over in split ARCADE: player finished but other still playing. */
  player1GameOver: boolean;
  player2GameOver: boolean;
};

const initial: FruitNinjaRemoteUiSnapshot = {
  hudVisible: false,
  pauseVisible: false,
  gameOverVisible: false,
  score: 0,
  pauseScore: 0,
  gameOverScore: 0,
  lives: null,
  timeLeftSec: null,
  timerUrgent: false,
  gameMode: null,
  streak: 0,
  comboCount: null,
  comboNormX: null,
  comboNormY: null,
  combo2Count: null,
  combo2NormX: null,
  combo2NormY: null,
  bestScore: null,
  split: false,
  player1Score: null,
  player1Lives: null,
  player1Streak: null,
  player2Score: null,
  player2Lives: null,
  player2Streak: null,
  player1GameOver: false,
  player2GameOver: false,
};

let snapshot: FruitNinjaRemoteUiSnapshot = { ...initial };
const listeners = new Set<() => void>();

/** Float coords from layout math drift frame-to-frame; strict `===` forced 60 React updates/s with combo. */
function normFracClose(a: number | null, b: number | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return Math.abs(a - b) < 0.0005;
}

function same(a: FruitNinjaRemoteUiSnapshot, b: FruitNinjaRemoteUiSnapshot): boolean {
  return (
    a.hudVisible === b.hudVisible &&
    a.pauseVisible === b.pauseVisible &&
    a.gameOverVisible === b.gameOverVisible &&
    a.score === b.score &&
    a.pauseScore === b.pauseScore &&
    a.gameOverScore === b.gameOverScore &&
    a.lives === b.lives &&
    a.timeLeftSec === b.timeLeftSec &&
    a.timerUrgent === b.timerUrgent &&
    a.gameMode === b.gameMode &&
    a.streak === b.streak &&
    a.comboCount === b.comboCount &&
    normFracClose(a.comboNormX, b.comboNormX) &&
    normFracClose(a.comboNormY, b.comboNormY) &&
    a.combo2Count === b.combo2Count &&
    normFracClose(a.combo2NormX, b.combo2NormX) &&
    normFracClose(a.combo2NormY, b.combo2NormY) &&
    a.bestScore === b.bestScore &&
    a.split === b.split &&
    a.player1Score === b.player1Score &&
    a.player1Lives === b.player1Lives &&
    a.player1Streak === b.player1Streak &&
    a.player2Score === b.player2Score &&
    a.player2Lives === b.player2Lives &&
    a.player2Streak === b.player2Streak &&
    a.player1GameOver === b.player1GameOver &&
    a.player2GameOver === b.player2GameOver
  );
}

export function getFruitNinjaRemoteUiSnapshot(): FruitNinjaRemoteUiSnapshot {
  return snapshot;
}

export function subscribeFruitNinjaRemoteUi(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
}

export function setFruitNinjaRemoteUiSnapshot(next: FruitNinjaRemoteUiSnapshot): void {
  if (same(snapshot, next)) return;
  snapshot = { ...next };
  listeners.forEach((l) => l());
}

export function patchFruitNinjaRemoteUi(patch: Partial<FruitNinjaRemoteUiSnapshot>): void {
  setFruitNinjaRemoteUiSnapshot({ ...snapshot, ...patch });
}

export function resetFruitNinjaRemoteUi(): void {
  setFruitNinjaRemoteUiSnapshot({ ...initial });
}

/** HUD + pause only (game over unchanged) — same as legacy hideFruitRemoteUi. */
export function hideFruitRemoteHudAndPause(): void {
  patchFruitNinjaRemoteUi({ hudVisible: false, pauseVisible: false });
}
